/* eslint-disable no-magic-numbers */

import { SOCIAL_AD_TYPES } from "utils/constants";
import { FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH, FACEBOOK_MAX_DISCLAIMER_URL_LENGTH, PAC_FORM_TYPES } from "social/constants/common";
import {
	isURL,
	isProtocolOptionalURL,
	maxLength,
	requiredNotNull
} from "widgets/forms/validators/validators";
import {
	dimensions,
	filesize,
	duration
} from "widgets/forms/validators/Assets";
import { GIGA, MEGA, SECONDS_PER_MIN } from "utils/common";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import {
	conditionalRequiredNotNull,
	errorMessageReducer,
	requiredAPIOption,
	requiredAsset,
	makeDynamicConfig,
	getCardWarnings,
	minCards,
	cardValidator,
	validateUnlessExternalRender,
	conditionalValidator,
	requiredWithImageType,
	requiredWithVideoType,
	requiredWithDynamicImageType,
	requiredWithDynamicVideoType, requiredImposiumProject, requiredImposiumComp,
	requiredAfterEffectsPackage, requiredAfterEffectsComp
} from "social/validators/validators";
import {
	mergeLists,
	uniqueVariableName,
	uniquePACVariableName,
	uniqueCardVariableName
} from "social/validators/utils";
import { imageMaxBytes } from "social/utils";

import * as IMAGE_SQUARE from "social/formats/FacebookImage/placements/Square/validators";
import * as IMAGE_HORIZONTAL from "social/formats/FacebookImage/placements/Horizontal/validators";
import * as IMAGE_VERTICAL from "social/formats/FacebookImage/placements/Vertical/validators";

import * as VIDEO_SQUARE from "social/formats/FacebookVideo/placements/Square/validators";
import * as VIDEO_HORIZONTAL from "social/formats/FacebookVideo/placements/Horizontal/validators";
import * as VIDEO_VERTICAL from "social/formats/FacebookVideo/placements/Vertical/validators";

import * as IMAGE_MO_SQUARE from "social/formats/FacebookImageMO/placements/Square/validators";
import * as IMAGE_MO_HORIZONTAL from "social/formats/FacebookImageMO/placements/Horizontal/validators";
import * as IMAGE_MO_VERTICAL from "social/formats/FacebookImageMO/placements/Vertical/validators";
import * as IMAGE_MO_FB_STORIES from "social/formats/FacebookImageMO/placements/FacebookStories/validators";
import * as IMAGE_MO_LEGACY_REELS from "social/formats/FacebookImageMO/placements/LegacyReels/validators";
import * as IMAGE_MO_FB_FEEDS from "social/formats/FacebookImageMO/placements/FacebookFeeds/validators";
import * as IMAGE_MO_FB_REELS from "social/formats/FacebookImageMO/placements/FacebookReels/validators";
import * as IMAGE_MO_IG_FEEDS from "social/formats/FacebookImageMO/placements/InstagramFeeds/validators";
import * as IMAGE_MO_IG_REELS from "social/formats/FacebookImageMO/placements/InstagramReels/validators";
import * as IMAGE_MO_IG_EXPLORE_HOME from "widgets/social2/formats/FacebookImageMO/placements/InstagramExploreHome/validators";
import * as IMAGE_MO_IG_PROFILE_FEELS from "social/formats/FacebookImageMO/placements/InstagramProfileReels/validators";
import * as IMAGE_MO_IG_STORIES from "social/formats/FacebookImageMO/placements/InstagramStories/validators";
import * as IMAGE_MO_IG_EXPLORE from "social/formats/FacebookImageMO/placements/InstagramExplore/validators";
import * as IMAGE_MO_FB_INSTREAM from "social/formats/FacebookImageMO/placements/FacebookInStreamVideo/validators";

import * as VIDEO_MO_SQUARE from "social/formats/FacebookVideoMO/placements/Square/validators";
import * as VIDEO_MO_HORIZONTAL from "social/formats/FacebookVideoMO/placements/Horizontal/validators";
import * as VIDEO_MO_VERTICAL from "social/formats/FacebookVideoMO/placements/Vertical/validators";
import * as VIDEO_MO_FB_STORIES from "social/formats/FacebookVideoMO/placements/FacebookStories/validators";
import * as VIDEO_MO_LEGACY_REELS from "social/formats/FacebookVideoMO/placements/LegacyReels/validators";
import * as VIDEO_MO_FB_FEEDS from "social/formats/FacebookVideoMO/placements/FacebookFeeds/validators";
import * as VIDEO_MO_FB_REELS from "social/formats/FacebookVideoMO/placements/FacebookReels/validators";
import * as VIDEO_MO_IG_FEEDS from "social/formats/FacebookVideoMO/placements/InstagramFeeds/validators";
import * as VIDEO_MO_IG_REELS from "social/formats/FacebookVideoMO/placements/InstagramReels/validators";
import * as VIDEO_MO_IG_EXPLORE_HOME from "widgets/social2/formats/FacebookVideoMO/placements/InstagramExploreHome/validators";
import * as VIDEO_MO_IG_PROFILE_REELS from "social/formats/FacebookVideoMO/placements/InstagramProfileReels/validators";
import * as VIDEO_MO_IG_STORIES from "social/formats/FacebookVideoMO/placements/InstagramStories/validators";
import * as VIDEO_MO_IG_EXPLORE from "social/formats/FacebookVideoMO/placements/InstagramExplore/validators";
import * as VIDEO_MO_FB_INSTREAM from "social/formats/FacebookVideoMO/placements/FacebookInStreamVideo/validators";

export function requireActivePlacement(selectedPlacements, allValues) {
	if (
		!allValues.placementAssetCustomizationType
		|| allValues.placementAssetCustomizationType === "none"
		|| selectedPlacements?.length
	) { return; }

	const { placements = {} } = allValues;

	for (const placement of Object.values(placements)) {
		if (placement.status === "active") {
			// As soon as we find an active placement we can exit
			return;
		}
	}

	// If we get here, we have no active placements
	return "At least one is required in addition to the default";
}

export function placementErrorMessageReducer(validators, values, allValues = {}) {
	/**
	 * This function is nearly the same as the base message reducer. However, we return the errors exactly,
	 * instead of a keyed object.
	 *
	 * `values` is the values of the current placement
	 * `allValues` is the values of the entire creative (which is used for the default placement)
	 */
	let hasErrors = false;
	const errors = Object.keys(validators).reduce((prev, key) => {
		let message = "";

		for (const validator of validators[key]) {
			message = validator(values[key], allValues, {}, key);
			if (message) {
				hasErrors = true;
				return {
					...prev,
					[key]: message,
				};
			}
		}

		return prev;
	}, {});

	if (hasErrors) {
		return errors;
	}
}

export function placementsWarningHandler(fieldValidatorsByPlacement) {
	/**
	 * Uses the fieldValidators from an `adType` to compile and attach placement warnings
	 */
	return (allPlacementValues, allValues) => {
		const placements = Object.keys(allPlacementValues || {});
		let hasError = false;
		const warnings = placements.reduce((prev, placementType) => {
			const placementValues = allPlacementValues[placementType];
			if (placementValues.status !== "active") {
				return prev;
			}

			const validators = fieldValidatorsByPlacement[placementType] || {};
			const warnings = placementErrorMessageReducer(validators, placementValues, allValues);

			if (warnings) {
				hasError = true;
				return {
					...prev,
					[placementType]: warnings,
				};
			}

			return prev;
		}, {});

		if (hasError) {
			return warnings;
		}
	};
}

const requiredUnlessDisclaimerURLProvided = conditionalValidator("disclaimerEnabled",
	conditionalValidator(
		"disclaimerUrl",
		(value) => {
			if(requiredNotNull(value)){
				return "Field is required unless Disclaimer URL is provided.";
			}
		},
		url => !url
	)
);

const requiredUnlessDisclaimerTextProvided = conditionalValidator("disclaimerEnabled",
	conditionalValidator(
		"disclaimerText",
		(value) => {
			if(requiredNotNull(value)){
				return "Field is required unless Disclaimer Text is provided.";
			}
		},
		text => !text
	)
);

export const FACEBOOK_IMAGE_CONSTRAINTS = {
	MAX_IMAGE_BYTES: imageMaxBytes(),
	MAX_LENGTH_LANDING_PAGE: 800,
	DIMENSIONS: { minWidth: 254, minHeight: 133 },
	IMAGE_ACCEPT: ".jpg, .jpeg, .png, .gif",
};

export function facebookImageWarning(allValues, props) {
	const {
		MAX_IMAGE_BYTES,
		DIMENSIONS,
		MAX_LENGTH_LANDING_PAGE,
	} = FACEBOOK_IMAGE_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;
	const values = allValues[formName] || {};

	const fieldValidators = {
		image: [requiredAsset, filesize({ max: MAX_IMAGE_BYTES }), dimensions(DIMENSIONS)],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
		displayLink: [isProtocolOptionalURL],
		facebookPage: [requiredAPIOption],
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [placementsWarningHandler({
			[PAC_FORM_TYPES.SQUARE]: IMAGE_SQUARE.validators,
			[PAC_FORM_TYPES.HORIZONTAL]: IMAGE_HORIZONTAL.validators,
			[PAC_FORM_TYPES.VERTICAL]: IMAGE_VERTICAL.validators,
		})],
	};

	return errorMessageReducer(fieldValidators, values, formName);
}

export const FB_MO_IMAGE_VALIDATORS = {
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE)
	],
	cta: [
		requiredNotNull
	],
	displayLink: [
		isProtocolOptionalURL
	],
	image: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: FACEBOOK_IMAGE_CONSTRAINTS.MAX_IMAGE_BYTES,
		}),
		dimensions(FACEBOOK_IMAGE_CONSTRAINTS.DIMENSIONS)
	]),
};

const FACEBOOK_IMAGE_MO_WARNINGS = mergeLists(
	{
		image: [uniquePACVariableName],
		headline: [uniquePACVariableName],
		primaryText: [uniquePACVariableName],
		description: [uniquePACVariableName],
		landingPage: [uniquePACVariableName],
		displayLink: [uniquePACVariableName],
		cta: [uniquePACVariableName],
		disclaimerId: [conditionalRequiredNotNull("disclaimerEnabled")],
		disclaimerText: [
			requiredUnlessDisclaimerURLProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH)
		],
		disclaimerUrl: [
			requiredUnlessDisclaimerTextProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_URL_LENGTH),
			isURL
		],

		placements: [
			placementsWarningHandler({
				[PAC_FORM_TYPES.SQUARE]: IMAGE_MO_SQUARE.staticValidators,
				[PAC_FORM_TYPES.HORIZONTAL]: IMAGE_MO_HORIZONTAL.staticValidators,
				[PAC_FORM_TYPES.VERTICAL]: IMAGE_MO_VERTICAL.staticValidators,
				[PAC_FORM_TYPES.FB_STORIES]: IMAGE_MO_FB_STORIES.staticValidators,
				[PAC_FORM_TYPES.REELS]: IMAGE_MO_LEGACY_REELS.staticValidators,
				[PAC_FORM_TYPES.FB_FEEDS]: IMAGE_MO_FB_FEEDS.staticValidators,
				[PAC_FORM_TYPES.FB_REELS]: IMAGE_MO_FB_REELS.staticValidators,
				[PAC_FORM_TYPES.FB_INSTREAM]: IMAGE_MO_FB_INSTREAM.staticValidators,
				[PAC_FORM_TYPES.IG_FEEDS]: IMAGE_MO_IG_FEEDS.staticValidators,
				[PAC_FORM_TYPES.IG_REELS]: IMAGE_MO_IG_REELS.staticValidators,
				[PAC_FORM_TYPES.IG_EXPLORE_HOME]: IMAGE_MO_IG_EXPLORE_HOME.staticValidators,
				[PAC_FORM_TYPES.IG_PROFILE_REELS]: IMAGE_MO_IG_PROFILE_FEELS.staticValidators,
				[PAC_FORM_TYPES.IG_STORIES]: IMAGE_MO_IG_STORIES.staticValidators,
				[PAC_FORM_TYPES.IG_EXPLORE]: IMAGE_MO_IG_EXPLORE.staticValidators,
			}),
		],
	},
	makeDynamicConfig({
		...FB_MO_IMAGE_VALIDATORS,
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [
			placementsWarningHandler({
				[PAC_FORM_TYPES.SQUARE]: makeDynamicConfig(IMAGE_MO_SQUARE.validators),
				[PAC_FORM_TYPES.HORIZONTAL]: makeDynamicConfig(IMAGE_MO_HORIZONTAL.validators),
				[PAC_FORM_TYPES.VERTICAL]: makeDynamicConfig(IMAGE_MO_VERTICAL.validators),
				[PAC_FORM_TYPES.FB_STORIES]: makeDynamicConfig(IMAGE_MO_FB_STORIES.validators),
				[PAC_FORM_TYPES.REELS]: makeDynamicConfig(IMAGE_MO_LEGACY_REELS.validators),
				[PAC_FORM_TYPES.FB_FEEDS]: makeDynamicConfig(IMAGE_MO_FB_FEEDS.validators),
				[PAC_FORM_TYPES.FB_REELS]: makeDynamicConfig(IMAGE_MO_FB_REELS.validators),
				[PAC_FORM_TYPES.FB_INSTREAM]: makeDynamicConfig(IMAGE_MO_FB_INSTREAM.validators),
				[PAC_FORM_TYPES.IG_FEEDS]: makeDynamicConfig(IMAGE_MO_IG_FEEDS.validators),
				[PAC_FORM_TYPES.IG_REELS]: makeDynamicConfig(IMAGE_MO_IG_REELS.validators),
				[PAC_FORM_TYPES.IG_EXPLORE_HOME]: makeDynamicConfig(IMAGE_MO_IG_EXPLORE_HOME.validators),
				[PAC_FORM_TYPES.IG_PROFILE_REELS]: makeDynamicConfig(IMAGE_MO_IG_PROFILE_FEELS.validators),
				[PAC_FORM_TYPES.IG_STORIES]: makeDynamicConfig(IMAGE_MO_IG_STORIES.validators),
				[PAC_FORM_TYPES.IG_EXPLORE]: makeDynamicConfig(IMAGE_MO_IG_EXPLORE.validators),
			}),
		],
	})
);

export function facebookImageMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_IMAGE_MO_WARNINGS, values, formName);
	}
}

export function facebookImageValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookImageMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_IMAGE_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

export const FACEBOOK_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_VIDEO_BYTES: 2 * GIGA,
	DIMENSIONS: { minWidth: 120, minHeight: 120 },
	MIN_VIDEO_SEC: 1,
	MAX_VIDEO_MIN: 15,
	VIDEO_ACCEPT: ".mp4, .mov",
	THUMBNAIL_ACCEPT: ".jpg, .jpeg, .png, .gif",
	MAX_THUMBNAIL_BYTES: imageMaxBytes(10 * MEGA),
};

export function facebookVideoWarning(allValues, props) {
	const {
		MAX_LENGTH_LANDING_PAGE,
		MAX_VIDEO_BYTES,
		DIMENSIONS,
		MIN_VIDEO_SEC,
		MAX_VIDEO_MIN,
	} = FACEBOOK_VIDEO_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO;
	const values = allValues[formName] || {};

	const fieldValidators = {
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
		displayLink: [isProtocolOptionalURL],
		video: [
			requiredAsset,
			filesize({ max: MAX_VIDEO_BYTES }),
			dimensions(DIMENSIONS),
			duration({ min: MIN_VIDEO_SEC, max: MAX_VIDEO_MIN * SECONDS_PER_MIN }),
		],
		facebookPage: [requiredAPIOption],
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [placementsWarningHandler({
			[PAC_FORM_TYPES.SQUARE]: VIDEO_SQUARE.validators,
			[PAC_FORM_TYPES.HORIZONTAL]: VIDEO_HORIZONTAL.validators,
			[PAC_FORM_TYPES.VERTICAL]: VIDEO_VERTICAL.validators,
		})],
	};

	return errorMessageReducer(fieldValidators, values, formName);
}

export const FB_MO_VIDEO_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	afterEffectsPackage: [
		requiredAfterEffectsPackage,
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp,
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull,
	],
	displayLink: [
		isProtocolOptionalURL,
	],
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_VIDEO_BYTES,
		}),
		dimensions(FACEBOOK_VIDEO_CONSTRAINTS.DIMENSIONS),
		duration({
			min: FACEBOOK_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
	]),
	thumbnail: validateUnlessExternalRender([
		filesize({
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_BYTES,
		}),
	]),
};

let FACEBOOK_VIDEO_MO_WARNINGS = mergeLists(
	{
		video: [uniquePACVariableName],
		thumbnail: [uniquePACVariableName],
		headline: [uniquePACVariableName],
		primaryText: [uniquePACVariableName],
		description: [uniquePACVariableName],
		landingPage: [uniquePACVariableName],
		displayLink: [uniquePACVariableName],
		cta: [uniquePACVariableName],
		disclaimerId: [conditionalRequiredNotNull("disclaimerEnabled")],
		disclaimerText: [
			requiredUnlessDisclaimerURLProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH)
		],
		disclaimerUrl: [
			requiredUnlessDisclaimerTextProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_URL_LENGTH),
			isURL
		],

		placements: [
			placementsWarningHandler({
				[PAC_FORM_TYPES.SQUARE]: VIDEO_MO_SQUARE.staticValidators,
				[PAC_FORM_TYPES.HORIZONTAL]: VIDEO_MO_HORIZONTAL.staticValidators,
				[PAC_FORM_TYPES.VERTICAL]: VIDEO_MO_VERTICAL.staticValidators,
				[PAC_FORM_TYPES.FB_STORIES]: VIDEO_MO_FB_STORIES.staticValidators,
				[PAC_FORM_TYPES.REELS]: VIDEO_MO_LEGACY_REELS.staticValidators,
				[PAC_FORM_TYPES.FB_FEEDS]: VIDEO_MO_FB_REELS.staticValidators,
				[PAC_FORM_TYPES.FB_REELS]: VIDEO_MO_FB_REELS.staticValidators,
				[PAC_FORM_TYPES.FB_INSTREAM]: VIDEO_MO_FB_INSTREAM.staticValidators,
				[PAC_FORM_TYPES.IG_FEEDS]: VIDEO_MO_IG_FEEDS.staticValidators,
				[PAC_FORM_TYPES.IG_REELS]: VIDEO_MO_IG_REELS.staticValidators,
				[PAC_FORM_TYPES.IG_EXPLORE_HOME]: VIDEO_MO_IG_EXPLORE_HOME.staticValidators,
				[PAC_FORM_TYPES.IG_PROFILE_REELS]: VIDEO_MO_IG_PROFILE_REELS.staticValidators,
				[PAC_FORM_TYPES.IG_STORIES]: VIDEO_MO_IG_STORIES.staticValidators,
				[PAC_FORM_TYPES.IG_EXPLORE]: VIDEO_MO_IG_EXPLORE.staticValidators,
			}),
		],
	},
	makeDynamicConfig({
		...FB_MO_VIDEO_VALIDATORS,
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [
			placementsWarningHandler({
				[PAC_FORM_TYPES.SQUARE]: makeDynamicConfig(VIDEO_MO_SQUARE.validators),
				[PAC_FORM_TYPES.HORIZONTAL]: makeDynamicConfig(VIDEO_MO_HORIZONTAL.validators),
				[PAC_FORM_TYPES.VERTICAL]: makeDynamicConfig(VIDEO_MO_VERTICAL.validators),
				[PAC_FORM_TYPES.FB_STORIES]: makeDynamicConfig(VIDEO_MO_FB_STORIES.validators),
				[PAC_FORM_TYPES.REELS]: makeDynamicConfig(VIDEO_MO_LEGACY_REELS.validators),
				[PAC_FORM_TYPES.FB_FEEDS]: makeDynamicConfig(VIDEO_MO_FB_FEEDS.validators),
				[PAC_FORM_TYPES.FB_REELS]: makeDynamicConfig(VIDEO_MO_FB_REELS.validators),
				[PAC_FORM_TYPES.FB_INSTREAM]: makeDynamicConfig(VIDEO_MO_FB_INSTREAM.validators),
				[PAC_FORM_TYPES.IG_FEEDS]: makeDynamicConfig(VIDEO_MO_IG_FEEDS.validators),
				[PAC_FORM_TYPES.IG_REELS]: makeDynamicConfig(VIDEO_MO_IG_REELS.validators),
				[PAC_FORM_TYPES.IG_EXPLORE_HOME]: makeDynamicConfig(VIDEO_MO_IG_EXPLORE_HOME.validators),
				[PAC_FORM_TYPES.IG_PROFILE_REELS]: makeDynamicConfig(VIDEO_MO_IG_PROFILE_REELS.validators),
				[PAC_FORM_TYPES.IG_STORIES]: makeDynamicConfig(VIDEO_MO_IG_STORIES.validators),
				[PAC_FORM_TYPES.IG_EXPLORE]: makeDynamicConfig(VIDEO_MO_IG_EXPLORE.validators),
			}),
		],
	})
);

export function facebookVideoMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_VIDEO_MO_WARNINGS, values, formName);
	}
}

const requiredWithSeeMoreCard = conditionalRequiredNotNull("seeMoreCard");

export function facebookCarouselWarning(allValues, props) {
	const {
		MAX_LENGTH_LANDING_PAGE,
	} = FACEBOOK_VIDEO_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL;
	const values = allValues[formName] || {};

	const warn = {
		facebookPage: [requiredAPIOption],
		seeMoreDisplayLink: [isProtocolOptionalURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		seeMoreDisplayUrl: [requiredWithSeeMoreCard, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
	};

	const mainWarnings = errorMessageReducer(warn, values, formName);

	const { cards = []} = values;
	const cardValidators = {
		image: [requiredWithImageType, requiredWithVideoType],
		video: [requiredWithVideoType],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
	};

	const allWarnings = {
		...mainWarnings?.[formName],
	};

	const cardWarnings = getCardWarnings(cards, values, cardValidators);
	if(cardWarnings.length){
		allWarnings.cards = cardWarnings;
	}

	const cardCount = minCards(SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL)(cards, values);
	if(cardCount){
		allWarnings.cardCount = cardCount;
	}

	if(Object.keys(allWarnings).length){
		return { [formName]: allWarnings };
	}
}
export const FACEBOOK_CAROUSEL_MO_CARD_VALIDATORS = {
	image: validateUnlessExternalRender([
		requiredWithDynamicImageType,
		requiredWithDynamicVideoType,
	]),
	video: validateUnlessExternalRender([
		requiredWithDynamicVideoType,
	]),
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	afterEffectsPackage: [
		requiredAfterEffectsPackage
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp
	],
};

const FACEBOOK_CAROUSEL_MO_CARD_WARNINGS = mergeLists(
	{
		image: [uniqueCardVariableName],
		video: [uniqueCardVariableName],
		headline: [uniqueCardVariableName],
		description: [uniqueCardVariableName],
		landingPage: [uniqueCardVariableName],
	},
	makeDynamicConfig({ ...FACEBOOK_CAROUSEL_MO_CARD_VALIDATORS })
);

export const FACEBOOK_CAROUSEL_MO_VALIDATORS = {
	seeMoreDisplayLink: [
		isProtocolOptionalURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	seeMoreDisplayUrl: [
		conditionalValidator("seeMoreCard", requiredNotNull, seeMoreCard => seeMoreCard?.value),
		conditionalValidator("seeMoreCard", isURL, seeMoreCard => seeMoreCard?.value),
		conditionalValidator("seeMoreCard", maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE), seeMoreCard => seeMoreCard?.value),
	],
	cta: [
		requiredNotNull,
	],
	cards: [
		cardValidator(FACEBOOK_CAROUSEL_MO_CARD_WARNINGS),
	],
	cardCount: [
		minCards(SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO),
	],
};

const FACEBOOK_CAROUSEL_MO_WARNINGS = mergeLists(
	{
		primaryText: [uniqueVariableName],
		disclaimerId: [conditionalRequiredNotNull("disclaimerEnabled")],
		disclaimerText: [
			requiredUnlessDisclaimerURLProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH)
		],
		disclaimerUrl: [
			requiredUnlessDisclaimerTextProvided,
			maxLength(FACEBOOK_MAX_DISCLAIMER_URL_LENGTH),
			isURL
		],
		cta: [uniqueVariableName],
		seeMoreDisplayLink: [conditionalValidator("seeMoreCard", uniqueVariableName, seeMoreCard => seeMoreCard?.value)],
		seeMoreDisplayUrl: [conditionalValidator("seeMoreCard", uniqueVariableName, seeMoreCard => seeMoreCard?.value)],
	},
	makeDynamicConfig({
		...FACEBOOK_CAROUSEL_MO_VALIDATORS,
	})
);

export function facebookCarouselMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_CAROUSEL_MO_WARNINGS, values, formName);
	}
}

export function facebookVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookVideoMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_VIDEO_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

export function facebookCarouselValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookCarouselMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_CAROUSEL_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
